import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createOperationalControl,
  listOperationalControls,
  listOperationalControlTargets,
  OperationalControl,
  updateOperationalControl,
} from "../../api/operationalControls";
import {
  CheckBoxWithLabel,
  Input,
  Label,
  TextArea,
} from "../../components/shared/InputField";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { Select } from "../../components/shared/Select";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSize,
  HStack,
  IconButtonType,
  IconCTAButton,
  IconSize,
  VStack,
} from "../../components/utils";
import { useCompanyStore } from "../../store";
import BCHeader from "../../shared/BCHeader";
import { displayOperationalControlValue } from "./display";

type DraftPolicy = {
  incomingAccess: "ALLOW" | "READ_ONLY" | "BLOCK";
  outboundCommunication: "ALLOW" | "HOLD";
  automation: "ALLOW" | "PAUSE";
  sessionAction: "GRACEFUL_LOCK" | "REVOKE_NOW";
  employeeAccessMessage?: string;
  adminAccessMessage?: string;
};

const initialPolicy: DraftPolicy = {
  incomingAccess: "BLOCK",
  outboundCommunication: "HOLD",
  automation: "PAUSE",
  sessionAction: "GRACEFUL_LOCK",
};

const targetIdsFor = (control: OperationalControl, type: string) =>
  control.targets
    .filter((target) => target.targetType === type && target.targetId)
    .map((target) => String(target.targetId));

const toDateTimeLocal = (value: string) => {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export default function CreateOperationalControl() {
  const companyId = useCompanyStore(
    (state) =>
      state.companyData?.id || sessionStorage.getItem("companyId") || ""
  );
  const navigate = useNavigate();
  const { controlId } = useParams<{ controlId?: string }>();
  const [searchParams] = useSearchParams();
  const sourceControlId = searchParams.get("sourceControlId") || undefined;
  const editing = Boolean(controlId);
  const cloning = Boolean(sourceControlId) && !editing;
  const [includeCompany, setIncludeCompany] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [reasonCode, setReasonCode] = useState("RULEZERO_MANUAL");
  const [activation, setActivation] = useState<"NOW" | "SCHEDULED">("NOW");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [policy, setPolicy] = useState<DraftPolicy>(initialPolicy);
  const [activeStep, setActiveStep] = useState<"SCOPE" | "RESTRICTIONS">(
    "SCOPE"
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const targets = useQuery(
    ["operational-control-targets", companyId],
    () => listOperationalControlTargets(companyId),
    { enabled: Boolean(companyId) }
  );
  const controls = useQuery(
    ["operational-controls", companyId],
    () => listOperationalControls(companyId),
    { enabled: Boolean(companyId) && (editing || cloning) }
  );
  const control = controls.data?.find(
    (candidate) => candidate.id === (controlId || sourceControlId)
  );

  useEffect(() => {
    if (!control) return;
    setIncludeCompany(
      control.targets.some((target) => target.targetType === "COMPANY")
    );
    setRoleIds(targetIdsFor(control, "ROLE"));
    setUserIds(targetIdsFor(control, "USER"));
    setEmployeeIds(targetIdsFor(control, "EMPLOYEE"));
    setReasonCode(control.reasonCode);
    setActivation(
      cloning || control.status === "RELEASED"
        ? "NOW"
        : control.status === "SCHEDULED"
        ? "SCHEDULED"
        : "NOW"
    );
    setEffectiveAt(
      cloning || control.status === "RELEASED"
        ? ""
        : toDateTimeLocal(control.effectiveAt)
    );
    setPolicy({
      incomingAccess: control.policy.incomingAccess || "BLOCK",
      outboundCommunication: control.policy.outboundCommunication || "HOLD",
      automation: control.policy.automation || "PAUSE",
      sessionAction: control.policy.sessionAction || "GRACEFUL_LOCK",
      employeeAccessMessage: control.policy.employeeAccessMessage || "",
      adminAccessMessage: control.policy.adminAccessMessage || "",
    });
  }, [cloning, control]);

  const selectedTargetCount =
    Number(includeCompany) +
    roleIds.length +
    userIds.length +
    employeeIds.length;
  const targetInputs = useMemo(
    () => [
      ...(includeCompany ? [{ type: "COMPANY", id: companyId }] : []),
      ...roleIds.map((id) => ({ type: "ROLE", id })),
      ...userIds.map((id) => ({ type: "USER", id })),
      ...employeeIds.map((id) => ({ type: "EMPLOYEE", id })),
    ],
    [companyId, employeeIds, includeCompany, roleIds, userIds]
  );
  const updatePolicy = <K extends keyof DraftPolicy>(
    key: K,
    value: DraftPolicy[K]
  ) => setPolicy((current) => ({ ...current, [key]: value }));
  const validate = () => {
    if (!targetInputs.length) {
      toast.error("Select at least one target for this control");
      return false;
    }
    if (activation === "SCHEDULED" && !effectiveAt) {
      toast.error("Choose when the scheduled control should take effect");
      return false;
    }
    if (
      activation === "SCHEDULED" &&
      new Date(effectiveAt).getTime() <= Date.now()
    ) {
      toast.error("Choose a future time for the scheduled control");
      return false;
    }
    if (
      [policy.employeeAccessMessage, policy.adminAccessMessage].some(
        (message) => (message || "").trim().length > 500
      )
    ) {
      toast.error("Affected-user messages cannot exceed 500 characters");
      return false;
    }
    return true;
  };
  const continueToRestrictions = () => {
    if (!targetInputs.length) {
      toast.error("Select at least one target for this control");
      return;
    }
    setActiveStep("RESTRICTIONS");
  };
  const activate = async () => {
    setSaving(true);
    try {
      const payload = {
        reasonCode,
        targets: targetInputs,
        policy,
        ...(activation === "SCHEDULED"
          ? { effectiveAt: new Date(effectiveAt).toISOString() }
          : {}),
      };
      if (controlId) {
        if (!control) {
          toast.error("The operational control is no longer available");
          return;
        }
        await updateOperationalControl(
          companyId,
          controlId,
          payload,
          control.revision
        );
      } else {
        await createOperationalControl(companyId, payload);
      }
      toast.success(
        editing
          ? control?.status === "RELEASED"
            ? "Operational control reactivated"
            : "Operational control updated"
          : cloning
          ? "New operational control created from clone"
          : "Operational control activated"
      );
      navigate("/options/operational-controls");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.reason ||
          (editing
            ? "We couldn't save the operational control"
            : "We couldn't activate the operational control")
      );
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  if (!companyId) {
    return (
      <Box className="p-6 text-sm text-secondaryText">
        Choose a company before creating an operational control.
      </Box>
    );
  }

  if ((editing || cloning) && controls.isLoading) {
    return (
      <Box className="p-6 text-sm text-secondaryText">
        Loading operational control...
      </Box>
    );
  }

  if ((editing || cloning) && controls.isSuccess && !control) {
    return (
      <VStack className="w-full gap-4">
        <BCHeader
          bcTitle="Operational controls"
          bcSubTitle="Edit control"
          className="py-1"
          onClick={() => navigate("/options/operational-controls")}
        />
        <Box className="rounded-lg border border-borderColor bg-white p-6">
          <VStack className="gap-3">
            <h1 className="text-xl font-semibold leading-7 text-headerColor">
              Operational control unavailable
            </h1>
            <p className="text-sm text-secondaryText">
              This control no longer exists or is not available for this
              company.
            </p>
            <ButtonPrimary1
              buttonSize={ButtonSize.MEDIUM}
              onClick={() => navigate("/options/operational-controls")}
            >
              Back to controls
            </ButtonPrimary1>
          </VStack>
        </Box>
      </VStack>
    );
  }

  const roleOptions = (targets.data?.roles || []).map((role) => ({
    value: role.id,
    label: role.name,
  }));
  const userOptions = (targets.data?.users || []).map((user) => ({
    value: String(user.id),
    label: `${user.full_name || "Unnamed user"} (${user.email_id || user.id})`,
  }));
  const employeeOptions = (targets.data?.employees || []).map((employee) => ({
    value: employee.id,
    label: `${employee.name} (${employee.email || employee.hrId})`,
  }));

  return (
    <main>
      <VStack className="w-full gap-6">
        <BCHeader
          bcTitle="Operational controls"
          bcSubTitle={
            editing
              ? control?.status === "RELEASED"
                ? "Reactivate control"
                : "Edit control"
              : cloning
              ? "Clone control"
              : "New control"
          }
          className="py-1"
          onClick={() => navigate("/options/operational-controls")}
        />
        <VStack className="gap-1">
          <h1 className="text-xl font-semibold leading-7 text-headerColor">
            {editing
              ? control?.status === "RELEASED"
                ? "Reactivate operational control"
                : "Edit operational control"
              : cloning
              ? "Create control from clone"
              : "Create new control"}
          </h1>
          <p className="max-w-3xl text-sm text-secondaryText">
            {editing
              ? control?.status === "RELEASED"
                ? "Review and update the targets and restrictions before reactivating this control."
                : "Update targets and restrictions. Every change is audited and takes effect after policy invalidation."
              : cloning
              ? "Review the cloned targets and restrictions before creating a separate control. The original control remains unchanged."
              : "Create an emergency control for this company. Each selected target is additive. A request is affected when it matches any selected company, role, user, or employee target."}
          </p>
        </VStack>
        <ControlStepper activeStep={activeStep} />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <VStack className="gap-6 lg:col-span-2">
            {activeStep === "SCOPE" ? (
              <Box className="rounded-lg border border-borderColor bg-white p-6">
                <VStack className="gap-5">
                  <SectionHeading
                    title="Scope and targets"
                    description={
                      "Targets are combined as a union, not an intersection. " +
                      "A matching company, role, user, or employee is affected."
                    }
                  />
                  <VStack className="gap-2 rounded-lg border border-inputBorder bg-tertiaryBg p-4">
                    <CheckBoxWithLabel
                      id="operational-company-target"
                      checked={includeCompany}
                      onChange={(event) =>
                        setIncludeCompany(event.target.checked)
                      }
                      label="Include the entire company"
                      labelclassname="ml-3 text-sm font-medium text-primaryText"
                      className="form-checkbox"
                    />
                    <p className="pl-6 text-xs text-secondaryText">
                      This affects every non-Rulezero identity in the company.
                      Use it only when a company-wide restriction is intended.
                    </p>
                  </VStack>
                  <TargetMultiSelect
                    label="Roles"
                    description="Applies to every user assigned to any selected role in this company."
                    placeholder={
                      targets.isLoading
                        ? "Loading roles"
                        : "Search and select roles"
                    }
                    options={roleOptions}
                    values={roleIds}
                    disabled={targets.isLoading}
                    onChange={setRoleIds}
                  />
                  <TargetMultiSelect
                    label="Platform users"
                    description="Applies to the selected administrator or platform user accounts."
                    placeholder={
                      targets.isLoading
                        ? "Loading users"
                        : "Search and select users"
                    }
                    options={userOptions}
                    values={userIds}
                    disabled={targets.isLoading}
                    onChange={setUserIds}
                  />
                  <TargetMultiSelect
                    label="Employees"
                    description="Applies to selected employee portal accounts."
                    placeholder={
                      targets.isLoading
                        ? "Loading employees"
                        : "Search and select employees"
                    }
                    options={employeeOptions}
                    values={employeeIds}
                    disabled={targets.isLoading}
                    onChange={setEmployeeIds}
                  />
                </VStack>
              </Box>
            ) : (
              <VStack className="gap-6">
                <Box className="rounded-lg border border-borderColor bg-white p-6">
                  <VStack className="gap-5">
                    <SectionHeading
                      title="Activation and reason"
                      description={
                        "Active controls are evaluated at access, communication, " +
                        "and automation boundaries. Scheduled controls use a " +
                        "durable activation job."
                      }
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field
                        label="Activation"
                        id="operational-activation"
                        value={activation}
                        options={["NOW", "SCHEDULED"]}
                        onChange={(value) =>
                          setActivation(value as "NOW" | "SCHEDULED")
                        }
                      />
                      {activation === "SCHEDULED" && (
                        <VStack className="gap-2">
                          <Label htmlFor="operational-effective-at">
                            Effective at
                          </Label>
                          <Input
                            id="operational-effective-at"
                            type="datetime-local"
                            value={effectiveAt}
                            onChange={(event) =>
                              setEffectiveAt(event.target.value)
                            }
                            required
                          />
                        </VStack>
                      )}
                    </div>
                    <Field
                      label="Reason"
                      id="operational-reason"
                      value={reasonCode}
                      options={[
                        "RULEZERO_MANUAL",
                        "SECURITY_INCIDENT",
                        "COMPLIANCE_REVIEW",
                        "OTHER_RULEZERO_APPROVED",
                      ]}
                      onChange={setReasonCode}
                    />
                  </VStack>
                </Box>

                <Box className="rounded-lg border border-borderColor bg-white p-6">
                  <VStack className="gap-5">
                    <SectionHeading
                      title="Restrictions matrix"
                      description={
                        "Choose the policy applied to every matching target. " +
                        "The strictest policy wins if several controls match."
                      }
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <PolicyField
                        label="Access"
                        id="operational-access"
                        value={policy.incomingAccess}
                        options={["ALLOW", "READ_ONLY", "BLOCK"]}
                        description="Allow changes nothing. Read-only blocks changes. Block denies access."
                        onChange={(value) =>
                          updatePolicy(
                            "incomingAccess",
                            value as DraftPolicy["incomingAccess"]
                          )
                        }
                      />
                      <PolicyField
                        label="Blocked session"
                        id="operational-session"
                        value={policy.sessionAction}
                        options={["GRACEFUL_LOCK", "REVOKE_NOW"]}
                        description="Graceful lock signs out employee sessions after 60 seconds. Revoke now disconnects them immediately."
                        onChange={(value) =>
                          updatePolicy(
                            "sessionAction",
                            value as DraftPolicy["sessionAction"]
                          )
                        }
                      />
                      <PolicyField
                        label="Communications"
                        id="operational-communication"
                        value={policy.outboundCommunication}
                        options={["ALLOW", "HOLD"]}
                        description="Hold retains final outgoing communications for Rulezero review before delivery."
                        onChange={(value) =>
                          updatePolicy(
                            "outboundCommunication",
                            value as DraftPolicy["outboundCommunication"]
                          )
                        }
                      />
                      <PolicyField
                        label="Automation"
                        id="operational-automation"
                        value={policy.automation}
                        options={["ALLOW", "PAUSE"]}
                        description="Pause stops protected scheduled and worker automation boundaries for matching targets."
                        onChange={(value) =>
                          updatePolicy(
                            "automation",
                            value as DraftPolicy["automation"]
                          )
                        }
                      />
                    </div>
                  </VStack>
                </Box>
                <Box className="rounded-lg border border-borderColor bg-white p-6">
                  <VStack className="gap-5">
                    <SectionHeading
                      title="Affected-user messages"
                      description="Optional plain-text messages shown only to the matching audience when access is blocked or read-only. Leave blank to use the standard message."
                    />
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <AudienceMessageField
                        id="operational-employee-access-message"
                        label="Employee portal message"
                        value={policy.employeeAccessMessage || ""}
                        onChange={(value) =>
                          updatePolicy("employeeAccessMessage", value)
                        }
                      />
                      <AudienceMessageField
                        id="operational-admin-access-message"
                        label="Administrator message"
                        value={policy.adminAccessMessage || ""}
                        onChange={(value) =>
                          updatePolicy("adminAccessMessage", value)
                        }
                      />
                    </div>
                  </VStack>
                </Box>
              </VStack>
            )}
          </VStack>
          {activeStep === "SCOPE" ? (
            <ScopeSummary
              includeCompany={includeCompany}
              roleCount={roleIds.length}
              userCount={userIds.length}
              employeeCount={employeeIds.length}
            />
          ) : (
            <ImpactSummary
              includeCompany={includeCompany}
              roleCount={roleIds.length}
              userCount={userIds.length}
              employeeCount={employeeIds.length}
              activation={activation}
              effectiveAt={effectiveAt}
              policy={policy}
            />
          )}
        </div>
        {activeStep === "SCOPE" ? (
          <HStack className="w-full flex-wrap justify-end gap-3 border-t border-borderColor pt-5">
            <ButtonPrimary1
              buttonSize={ButtonSize.MEDIUM}
              disabled={saving}
              onClick={() => navigate("/options/operational-controls")}
            >
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              buttonSize={ButtonSize.MEDIUM}
              disabled={!selectedTargetCount}
              onClick={continueToRestrictions}
            >
              Continue to restrictions
            </ButtonPrimary>
          </HStack>
        ) : (
          <HStack className="w-full flex-wrap items-center justify-between gap-3 border-t border-borderColor pt-5">
            <ButtonPrimary1
              buttonSize={ButtonSize.MEDIUM}
              disabled={saving}
              onClick={() => navigate("/options/operational-controls")}
            >
              Cancel
            </ButtonPrimary1>
            <HStack className="flex-wrap items-center gap-3">
              <ButtonPrimary1
                buttonSize={ButtonSize.MEDIUM}
                disabled={saving}
                onClick={() => setActiveStep("SCOPE")}
              >
                Back to scope
              </ButtonPrimary1>
              <ButtonPrimary
                buttonSize={ButtonSize.MEDIUM}
                disabled={!selectedTargetCount}
                onClick={() => validate() && setConfirmOpen(true)}
              >
                {editing
                  ? "Review changes"
                  : cloning
                  ? "Review new control"
                  : "Review activation"}
              </ButtonPrimary>
            </HStack>
          </HStack>
        )}
      </VStack>
      <ConfirmationDialog
        open={confirmOpen}
        saving={saving}
        targetCount={selectedTargetCount}
        activation={activation}
        effectiveAt={effectiveAt}
        policy={policy}
        actionLabel={
          editing
            ? control?.status === "RELEASED"
              ? "Reactivate control"
              : "Save changes"
            : "Activate control"
        }
        onClose={() => setConfirmOpen(false)}
        onConfirm={activate}
      />
    </main>
  );
}

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <VStack className="gap-1">
      <h2 className="text-base font-semibold leading-6 text-headerColor">
        {title}
      </h2>
      <p className="text-sm text-secondaryText">{description}</p>
    </VStack>
  );
}

function ControlStepper({
  activeStep,
}: {
  activeStep: "SCOPE" | "RESTRICTIONS";
}) {
  const restrictionsActive = activeStep === "RESTRICTIONS";

  return (
    <nav aria-label="Create operational control steps">
      <ol className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StepIndicator
          complete={restrictionsActive}
          current={!restrictionsActive}
          number="1"
          title="Scope and targets"
          description="Choose the company, roles, users, and employees affected"
        />
        <StepIndicator
          current={restrictionsActive}
          number="2"
          title="Restrictions matrix"
          description="Set access, communication, automation, and session actions"
        />
      </ol>
    </nav>
  );
}

function StepIndicator({
  complete = false,
  current = false,
  number,
  title,
  description,
}: {
  complete?: boolean;
  current?: boolean;
  number: string;
  title: string;
  description: string;
}) {
  const stateClass =
    current || complete
      ? "border-primary bg-white"
      : "border-borderColor bg-white";
  const markerClass =
    current || complete
      ? "bg-primary text-white"
      : "bg-tertiaryBg text-secondaryText";

  return (
    <li className={`rounded-lg border p-4 ${stateClass}`}>
      <HStack className="min-w-0 items-start gap-3">
        <span
          aria-hidden="true"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${markerClass}`}
        >
          {complete ? "✓" : number}
        </span>
        <VStack className="min-w-0 gap-1">
          <p className="text-sm font-semibold text-headerColor">{title}</p>
          <p className="text-xs text-secondaryText">{description}</p>
        </VStack>
      </HStack>
    </li>
  );
}

function ScopeSummary({
  includeCompany,
  roleCount,
  userCount,
  employeeCount,
}: {
  includeCompany: boolean;
  roleCount: number;
  userCount: number;
  employeeCount: number;
}) {
  const targetSummary = [
    includeCompany ? "entire company" : "",
    roleCount ? `${roleCount} role${roleCount === 1 ? "" : "s"}` : "",
    userCount ? `${userCount} user${userCount === 1 ? "" : "s"}` : "",
    employeeCount
      ? `${employeeCount} employee${employeeCount === 1 ? "" : "s"}`
      : "",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box className="h-fit rounded-lg border border-inputBorder bg-tertiaryBg p-5">
      <VStack className="gap-4">
        <SectionHeading
          title="Scope review"
          description="Confirm who this control will affect before continuing."
        />
        <ImpactItem
          label="Selected targets"
          value={targetSummary || "No targets selected"}
        />
        <ImpactItem
          label="How targeting works"
          value="Targets are additive. Any matching target is affected."
        />
        <p className="text-xs text-secondaryText">
          Rulezero operators retain access to manage the control.
        </p>
      </VStack>
    </Box>
  );
}

function TargetMultiSelect({
  label,
  description,
  placeholder,
  options,
  values,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  values: string[];
  disabled: boolean;
  onChange: (values: string[]) => void;
}) {
  const [selectedFilter, setSelectedFilter] = useState("");
  const selectedOptions = values.map((value) => {
    const option = options.find((candidate) => candidate.value === value);
    return option || { value, label: `Unavailable target (${value})` };
  });
  const normalizedFilter = selectedFilter.trim().toLowerCase();
  const filteredSelectedOptions = normalizedFilter
    ? selectedOptions.filter((option) =>
        option.label.toLowerCase().includes(normalizedFilter)
      )
    : selectedOptions;
  const selectedLabel = `Selected ${label.toLowerCase()}`;
  const selectedFilterId = `selected-${label
    .toLowerCase()
    .replace(/\s+/g, "-")}-filter`;

  useEffect(() => {
    if (selectedOptions.length <= 6 && selectedFilter) {
      setSelectedFilter("");
    }
  }, [selectedFilter, selectedOptions.length]);

  return (
    <VStack className="gap-3">
      <VStack className="gap-1">
        <Label>{label}</Label>
        <p className="text-xs text-secondaryText">{description}</p>
      </VStack>
      <MultiSelectV2
        multiple
        searchable
        placeholder={placeholder}
        options={options}
        optionValues={values}
        disabled={disabled}
        maxSelectedValueDisplay={1}
        handleSelection={(values) => onChange(values as string[])}
      />
      {selectedOptions.length > 0 && (
        <VStack className="gap-3 rounded-lg border border-inputBorder bg-tertiaryBg p-4">
          <HStack className="w-full flex-wrap items-center justify-between gap-3">
            <VStack className="min-w-0 gap-1">
              <p className="text-sm font-semibold text-headerColor">
                {selectedLabel}
              </p>
              <p aria-live="polite" className="text-xs text-secondaryText">
                {selectedOptions.length} selected. Remove individual targets or
                clear the full selection.
              </p>
            </VStack>
            <ButtonPrimary1
              buttonSize={ButtonSize.SMALL}
              disabled={disabled}
              onClick={() => {
                setSelectedFilter("");
                onChange([]);
              }}
            >
              Clear selection
            </ButtonPrimary1>
          </HStack>
          {selectedOptions.length > 6 && (
            <VStack className="gap-2">
              <Label htmlFor={selectedFilterId}>
                Filter selected {label.toLowerCase()}
              </Label>
              <Input
                id={selectedFilterId}
                value={selectedFilter}
                onChange={(event) => setSelectedFilter(event.target.value)}
                placeholder={`Filter ${label.toLowerCase()}`}
              />
            </VStack>
          )}
          {filteredSelectedOptions.length ? (
            <ul className="grid max-h-72 grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
              {filteredSelectedOptions.map((option) => (
                <li key={option.value}>
                  <Box className="h-full rounded-lg border border-borderColor bg-white p-3">
                    <HStack className="h-full min-w-0 items-start justify-between gap-2">
                      <p
                        className="min-w-0 break-words text-sm text-primaryText"
                        title={option.label}
                      >
                        {option.label}
                      </p>
                      <IconCTAButton
                        aria-label={`Remove ${option.label}`}
                        title={`Remove ${option.label}`}
                        iconName="material-symbols:close-rounded"
                        iconSize={IconSize.S}
                        iconOnly
                        buttonType={IconButtonType.NONE}
                        disabled={disabled}
                        onClick={() =>
                          onChange(
                            values.filter((value) => value !== option.value)
                          )
                        }
                      />
                    </HStack>
                  </Box>
                </li>
              ))}
            </ul>
          ) : (
            <Box className="rounded-lg border border-borderColor bg-white p-4 text-sm text-secondaryText">
              No selected {label.toLowerCase()} match this filter.
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
}

function Field({
  label,
  id,
  value,
  options,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <VStack className="gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        id={id}
        value={value}
        options={options}
        textGetter={displayOperationalControlValue}
        onChange={(event) => onChange(event.target.value)}
      />
    </VStack>
  );
}

function PolicyField({
  label,
  id,
  value,
  options,
  description,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  options: string[];
  description: string;
  onChange: (value: string) => void;
}) {
  return (
    <VStack className="gap-2">
      <Field
        label={label}
        id={id}
        value={value}
        options={options}
        onChange={onChange}
      />
      <p className="text-xs text-secondaryText">{description}</p>
    </VStack>
  );
}

function AudienceMessageField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <VStack className="gap-2">
      <HStack className="w-full items-center justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-xs text-secondaryText">{value.length}/500</span>
      </HStack>
      <TextArea
        id={id}
        maxLength={500}
        rows={4}
        value={value}
        placeholder="Use the standard message"
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-xs text-secondaryText">
        Plain text only. Do not include sensitive information or HTML.
      </p>
    </VStack>
  );
}

function ImpactSummary({
  includeCompany,
  roleCount,
  userCount,
  employeeCount,
  activation,
  effectiveAt,
  policy,
}: {
  includeCompany: boolean;
  roleCount: number;
  userCount: number;
  employeeCount: number;
  activation: "NOW" | "SCHEDULED";
  effectiveAt: string;
  policy: DraftPolicy;
}) {
  const targetSummary = [
    includeCompany ? "entire company" : "",
    roleCount ? `${roleCount} role${roleCount === 1 ? "" : "s"}` : "",
    userCount ? `${userCount} user${userCount === 1 ? "" : "s"}` : "",
    employeeCount
      ? `${employeeCount} employee${employeeCount === 1 ? "" : "s"}`
      : "",
  ]
    .filter(Boolean)
    .join(", ");
  const targetRuleCount =
    Number(includeCompany) + roleCount + userCount + employeeCount;
  const activationSummary =
    activation === "NOW"
      ? {
          value: "Effective immediately",
          description:
            "Policy cache state and connected clients are updated after activation.",
        }
      : {
          value: effectiveAt
            ? new Date(effectiveAt).toLocaleString()
            : "Choose a future time",
          description:
            "The durable activation job applies the policy at the selected time.",
        };
  const outcomes = policyOutcomes(policy);

  return (
    <Box className="h-fit rounded-lg border border-inputBorder bg-tertiaryBg p-5">
      <VStack className="gap-4">
        <SectionHeading
          title="Impact review"
          description="Review the effective outcome before activating the control."
        />
        <ImpactItem
          label="Affected scope"
          value={`${targetRuleCount} selected target rule${
            targetRuleCount === 1 ? "" : "s"
          }`}
          description={targetSummary || "No targets selected"}
        />
        <ImpactItem
          label="Effective time"
          value={activationSummary.value}
          description={activationSummary.description}
        />
        <VStack className="gap-3 rounded-lg border border-borderColor bg-white p-4">
          <VStack className="gap-1">
            <h3 className="text-sm font-semibold text-headerColor">
              Effective restrictions
            </h3>
            <p className="text-xs text-secondaryText">
              The strongest matching policy is applied at each protected
              boundary.
            </p>
          </VStack>
          <VStack className="gap-3">
            {outcomes.map((outcome) => (
              <ImpactOutcome key={outcome.label} {...outcome} />
            ))}
          </VStack>
        </VStack>
        {(policy.employeeAccessMessage || policy.adminAccessMessage) && (
          <ImpactItem
            label="Affected-user messages"
            value={[
              policy.employeeAccessMessage ? "Employee portal" : "",
              policy.adminAccessMessage ? "Administrator" : "",
            ]
              .filter(Boolean)
              .join(" and ")}
            description="Custom text replaces the standard access message for the listed audiences."
          />
        )}
        <p className="text-xs text-secondaryText">
          Existing controls can still make the final result stricter. Rulezero
          operators retain access to inspect and change this control.
        </p>
      </VStack>
    </Box>
  );
}

function policyOutcomes(policy: DraftPolicy) {
  const access =
    policy.incomingAccess === "BLOCK"
      ? {
          value: "Access blocked",
          description: "Affected people cannot access protected areas.",
        }
      : policy.incomingAccess === "READ_ONLY"
      ? {
          value: "Read-only access",
          description: "Affected people can view data but cannot make changes.",
        }
      : {
          value: "Access allowed",
          description: "This control does not add an access restriction.",
        };
  const communication =
    policy.outboundCommunication === "HOLD"
      ? {
          value: "Outgoing communications held",
          description: "New emails wait for Rulezero review before delivery.",
        }
      : {
          value: "Communications allowed",
          description: "This control does not hold outgoing communications.",
        };
  const automation =
    policy.automation === "PAUSE"
      ? {
          value: "Automation paused",
          description:
            "Protected jobs and workers do not process these targets.",
        }
      : {
          value: "Automation allowed",
          description: "This control does not pause protected automation.",
        };
  const session =
    policy.sessionAction === "REVOKE_NOW"
      ? {
          value: "Sessions revoked now",
          description:
            "Affected employee sessions are disconnected immediately.",
        }
      : {
          value: "Sessions lock after 60 seconds",
          description:
            "Affected employee sessions receive a short lockout window.",
        };

  return [
    { label: "Access", ...access },
    { label: "Communications", ...communication },
    { label: "Automation", ...automation },
    { label: "Sessions", ...session },
  ];
}

function ImpactItem({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}) {
  return (
    <VStack className="gap-1 rounded-lg bg-white p-3">
      <p className="text-xs font-semibold text-primaryText">{label}</p>
      <p className="text-sm text-headerColor">{value}</p>
      {description && (
        <p className="text-xs text-secondaryText">{description}</p>
      )}
    </VStack>
  );
}

function ImpactOutcome({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <VStack className="gap-1 border-l-2 border-primary pl-3">
      <p className="text-xs font-semibold text-primaryText">{label}</p>
      <p className="text-sm font-medium text-headerColor">{value}</p>
      <p className="text-xs text-secondaryText">{description}</p>
    </VStack>
  );
}

function ConfirmationDialog({
  open,
  saving,
  targetCount,
  activation,
  effectiveAt,
  policy,
  actionLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  saving: boolean;
  targetCount: number;
  activation: "NOW" | "SCHEDULED";
  effectiveAt: string;
  policy: DraftPolicy;
  actionLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        className={
          "border-b border-borderColor px-6 py-5 text-base font-semibold " +
          "text-headerColor"
        }
      >
        Confirm operational control
      </DialogTitle>
      <DialogContent className="px-6 py-5">
        <VStack className="gap-4">
          <p className="text-sm text-primaryText">
            You are about to apply this control to {targetCount} selected target
            {targetCount === 1 ? "" : "s"}. Targets are additive, so any
            matching target is affected.
          </p>
          <Box className="rounded-lg border border-inputBorder bg-tertiaryBg p-4">
            <VStack className="gap-2">
              <p className="text-sm font-semibold text-headerColor">
                {activation === "NOW"
                  ? "Effective immediately"
                  : "Scheduled activation"}
              </p>
              <p className="text-sm text-secondaryText">
                {activation === "NOW"
                  ? "Policy cache invalidation and connected-client " +
                    "notification start after activation."
                  : `The durable activation job runs at ${
                      effectiveAt
                        ? new Date(effectiveAt).toLocaleString()
                        : "the selected time"
                    }.`}
              </p>
              <p className="text-sm text-secondaryText">
                Access: {displayOperationalControlValue(policy.incomingAccess)}.
                Communications:{" "}
                {displayOperationalControlValue(policy.outboundCommunication)}.
                Automation: {displayOperationalControlValue(policy.automation)}.
                Session: {displayOperationalControlValue(policy.sessionAction)}.
              </p>
              {(policy.employeeAccessMessage || policy.adminAccessMessage) && (
                <p className="text-sm text-secondaryText">
                  Custom message:{" "}
                  {policy.employeeAccessMessage ? "employee portal" : ""}
                  {policy.employeeAccessMessage && policy.adminAccessMessage
                    ? " and "
                    : ""}
                  {policy.adminAccessMessage ? "administrator" : ""}.
                </p>
              )}
            </VStack>
          </Box>
          <p className="text-xs text-secondaryText">
            Rulezero operators retain the ability to inspect and release this
            control.
          </p>
        </VStack>
      </DialogContent>
      <DialogActions className="justify-between border-t border-borderColor px-6 py-4">
        <ButtonPrimary1
          buttonSize={ButtonSize.MEDIUM}
          disabled={saving}
          onClick={onClose}
        >
          Back
        </ButtonPrimary1>
        <ButtonPrimary
          buttonSize={ButtonSize.MEDIUM}
          loading={saving}
          loadingText="Saving"
          onClick={onConfirm}
        >
          {actionLabel}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
}
